import { useState, useEffect } from "react";
import { useCartData } from "@/zustand/cartStore";
import { useProductData } from "@/zustand/productStore";
import { formatPrice } from "@/func/priceNotation";

const useOrderCalculations = (pricingPackage, isDiscountApplied, MultiplyWarranty, warrantyState, formattedString) => {
  const [calculations, setCalculations] = useState(null);
  useEffect(() => {
    // static values not dependend to bundle design mode or coupon code
    const oldPrice = pricingPackage?.old_price;
    const productAmount = pricingPackage?.amount;
    const billedAmount = Number(pricingPackage?.billed_amount) ? pricingPackage?.billed_amount : productAmount;

    //   calculate product price

    const price = Number(pricingPackage?.price) * Number(billedAmount);

    //  calculate promo price

    let promoPrice = 0;

    if (isDiscountApplied) {
      promoPrice = Number(pricingPackage?.promo_discounted_price) * Number(billedAmount);
    }

    //   check if discount code is applied

    const productPrice = isDiscountApplied ? promoPrice : price;

    //   warranty price

    const warrantyPrice = !MultiplyWarranty
      ? Number(pricingPackage?.warranty_price)
      : Number(pricingPackage?.warranty_price) * Number(productAmount);
    const warranty = warrantyState ? warrantyPrice : 0;

    //   subtotal

    let subtotal = Number(productPrice) + Number(warranty);
    subtotal = subtotal.toFixed(2);

    //   shipping price

    let shippingPrice = isDiscountApplied
      ? Number(pricingPackage?.promo_shipping_price)
      : Number(pricingPackage?.shipping_price);
    shippingPrice = shippingPrice.toFixed(2);

    //   total

    let total = Number(subtotal) + Number(shippingPrice);
    total = total.toFixed(2);

    //   total savings

    let shippingDifference = 0;

    if (isDiscountApplied) {
      shippingDifference = Number(pricingPackage?.shipping_price) - Number(pricingPackage?.promo_shipping_price);
    }

    let totalSavings = Number(oldPrice) - Number(productPrice) + shippingDifference;

    if (pricingPackage?.enable_fake_products && pricingPackage?.fake_products?.length > 0) {
      const fakeProductsPrice = pricingPackage.fake_products.reduce((acc, fakeProduct) => {
        return acc + (Number(fakeProduct.price) || 0);
      }, 0);
      totalSavings += fakeProductsPrice;
    }

    totalSavings = totalSavings.toFixed(2);

    // format all amounts using formatted string and return

    setCalculations({
      savings: formattedString ? formatPrice(totalSavings, formattedString) : "0.00",
      subtotal: formattedString ? formatPrice(subtotal, formattedString) : "0.00",
      total: formattedString ? formatPrice(total, formattedString) : "0.00",
      shipping: formattedString ? formatPrice(shippingPrice, formattedString) : "0.00",
      warranty: formattedString ? formatPrice(warranty, formattedString) : "0.00",
      total_numeric: total,
    });
  }, [pricingPackage, formattedString, warrantyState, isDiscountApplied]);

  return calculations;
};

// Calculations for Order summary product card

const useCalculateProductCardPrice = (product, amount) => {
  const cartData = useCartData();
  const productData = useProductData();
  const [price, setPrice] = useState(null);
  const [promoPrice, setPromoPrice] = useState(null);
  const [oldPrice, setOldPrice] = useState(null);

  useEffect(() => {
    // amount - if passed as prop
    // product.amount - pack-level amount
    // product.billed_amount - pack-level for subscription
    const qty = amount ? amount : Number(product?.billed_amount) ? product?.billed_amount : product?.amount;

    let warrantyPrice = !productData?.multiply_price_by_amount
      ? Number(product.warranty_price)
      : Number(product.warranty_price) * Number(product.amount);

    if (amount && warrantyPrice) {
      const splittedByAmount = warrantyPrice / product?.amount;
      warrantyPrice = splittedByAmount * amount;
    }

    const warranty = cartData?.warrantyState ? warrantyPrice : 0;

    const priceToFormat = (Number(product?.price) * Number(qty) + Number(warranty)).toFixed(2);
    setPrice(priceToFormat);

    const promoPriceWithWarranty = (Number(product.promo_discounted_price) * Number(qty) + Number(warranty)).toFixed(2);
    setPromoPrice(promoPriceWithWarranty);

    const oldPriceCalculated = (
      Number(amount ? (product.old_price / product.amount) * amount : product.old_price) + Number(warranty)
    ).toFixed(2);

    const formattedOldPrice = formatPrice(oldPriceCalculated, productData?.formatted_string);
    setOldPrice(formattedOldPrice);
  }, [product, cartData?.warrantyState, cartData?.variants, cartData?.discountCode, amount]);

  return { oldPrice, price, promoPrice };
};

export { useOrderCalculations, useCalculateProductCardPrice };
